import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { api, regionTokenFor } from "./index";
import type { QueryIr, QueryResponse, ResultFor } from "./ir";
import type { EnvironmentName } from "./schemas";

export const keys = {
  me: ["me"] as const,
  regions: ["regions"] as const,
  projects: (orgId: string) => ["orgs", orgId, "projects"] as const,
  project: (id: string) => ["projects", id] as const,
  keys: (id: string) => ["projects", id, "keys"] as const,
  tokens: ["tokens"] as const,
  events: (projectId: string, env: EnvironmentName) => ["region", projectId, env, "events"] as const,
  properties: (projectId: string, env: EnvironmentName) => ["region", projectId, env, "properties"] as const,
  query: (projectId: string, env: EnvironmentName, ir: QueryIr) => ["region", projectId, env, "query", ir] as const,
};

export const meQuery = queryOptions({ queryKey: keys.me, queryFn: () => api.me(), staleTime: 60_000 });
export const regionsQuery = queryOptions({ queryKey: keys.regions, queryFn: () => api.regions(), staleTime: Infinity });
export const projectsQuery = (orgId: string) =>
  queryOptions({ queryKey: keys.projects(orgId), queryFn: () => api.listProjects(orgId), staleTime: 30_000 });
export const projectQuery = (id: string) =>
  queryOptions({ queryKey: keys.project(id), queryFn: () => api.getProject(id), staleTime: 60_000 });
export const keysQuery = (id: string) => queryOptions({ queryKey: keys.keys(id), queryFn: () => api.listKeys(id) });
export const tokensQuery = queryOptions({ queryKey: keys.tokens, queryFn: () => api.listTokens() });

export function useEvents(projectId: string, env: EnvironmentName) {
  return useQuery({
    queryKey: keys.events(projectId, env),
    queryFn: async () => api.events(await regionTokenFor(projectId, env)),
    staleTime: 5 * 60_000,
  });
}

export function useProperties(projectId: string, env: EnvironmentName) {
  return useQuery({
    queryKey: keys.properties(projectId, env),
    queryFn: async () => api.properties(await regionTokenFor(projectId, env)),
    staleTime: 5 * 60_000,
  });
}

/** Runs an insight against the project's region. Keeps the previous result on screen while a new one loads. */
export function useInsight<Q extends QueryIr>(projectId: string, env: EnvironmentName, ir: Q | null) {
  return useQuery({
    queryKey: ir ? keys.query(projectId, env, ir) : ["region", projectId, env, "query", "idle"],
    enabled: ir !== null,
    queryFn: async ({ signal }) => {
      if (!ir) throw new Error("no query");
      const rt = await regionTokenFor(projectId, env);
      return (await api.query(rt, ir, signal)) as QueryResponse<ResultFor<Q>>;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: 1,
  });
}
